// src/app/AccessDenied/roleRoutePermissions.js
//
// Sistema de control de acceso por ruta apoyado en /api/MasterData/role-modules.
//
// El backend devuelve para cada (employeeId, roleId) un array con TODOS los
// módulos del catálogo y un flag `hasAccess`. Ese array vive en
// `user.modules` (cargado en AuthContext.runPostLoginFlow / switchRole).
//
// Este archivo expone canRoleAccessRoute(user, pathname). Para decidir si una
// ruta está permitida:
//   1. Si la ruta es UNIVERSAL → permitir.
//   2. Si el rol activo es Admin/CEO → permitir (safety net; en condiciones
//      normales el RBAC matrix también les concede todos los módulos).
//   3. Si el roleKey activo está denegado explícitamente para la ruta
//      (ROUTE_DENIED_ROLE_KEYS) → denegar, aunque su matriz le conceda el
//      módulo que la gatea.
//   4. Mapear la ruta a un moduleKey del catálogo (TAB_MODULES). Si la ruta
//      no está mapeada → permitir (no hay gate).
//   5. Buscar el moduleKey en user.modules. hasAccess = true → permitir;
//      cualquier otra cosa → denegar.
//
// Si admin agrega un módulo nuevo al catálogo y le da acceso a un rol vía
// la matriz RBAC, basta con agregar el prefijo de ruta correspondiente al
// map ROUTE_TO_MODULE_KEY usando el mismo `moduleKey` que aparece en
// Administration → Access Control → Modules.

// Rutas SIN gate por diseño. La lista se mantiene mínima a propósito: cada
// entrada acá es un módulo que la matriz RBAC no puede gobernar.
//
// - /Main: es la landing post-login (LoginPage navega ahí y sus dashboards ya
//   son por rol). Gatearla con el módulo `dashboard` del catálogo dejaría a un
//   usuario sin ese módulo rebotando a Access Denied apenas entra, así que se
//   deja abierta a propósito.
// - /profile: el perfil propio del usuario, no un módulo.
// - /Login, /Unauthorized, /AccessDenied: pantallas del propio flujo de acceso.
//
// /support y /library salieron de esta lista (2026-08-31): ambos existen en el
// catálogo (`tech_support`, `library`) y ahora los gobierna la matriz RBAC.
const UNIVERSAL_ROUTES = [
  "/Main",
  "/main",
  "/profile",
  "/Login",
  "/Unauthorized",
  "/AccessDenied",
];

// Roles que tienen bypass de control de acceso. En el ideal el backend les
// concede todos los módulos vía la matriz RBAC y este bypass es redundante,
// pero se conserva como red de seguridad para no dejar a admin/CEO bloqueados
// si alguien revoca un módulo por error.
const FULL_ACCESS_ROLE_KEYS = new Set(["admin", "ceo"]);
const FULL_ACCESS_ROLE_IDS = new Set(["1", "4"]);

// Denegaciones explícitas por roleKey del backend. A diferencia de
// ROUTE_TO_MODULE_KEY (que concede por módulo del catálogo), esto RESTA acceso
// a un rol puntual aunque su matriz RBAC le conceda el módulo que gatea la
// ruta. Como NavigationSections.isItemVisible usa canUserAccessRoute, la
// denegación también saca el ítem del sidebar — un solo gate para ambos.
//
// VACÍO desde 2026-08-31. La única entrada que había ("/no-recent-visits" para
// el rol cca) era un parche: la ruta heredaba `clinical_info` y no había forma
// de quitársela a Contact Center sin quitarle Clinical Info. Ahora
// /no-recent-visits tiene su propio módulo (`no_recent_visits`, 36) y quién lo
// ve se decide en la matriz RBAC, no acá.
//
// Antes de agregar una entrada nueva: si el problema es "este rol no debería
// ver X", casi siempre la solución correcta es darle a X su propio moduleKey en
// el catálogo, no restarlo a mano acá.
const ROUTE_DENIED_ROLE_KEYS = {};

// Rutas restringidas a una lista explícita de empleados (pilotos de módulos que
// todavía no están en el catálogo de Access Control). Es la regla MÁS fuerte:
// se evalúa antes que las rutas universales y antes del bypass de admin/CEO, y
// como el sidebar usa este mismo gate, el ítem tampoco aparece para quien no
// esté en la lista.
//
// VACÍO desde 2026-08-31: CDCI era el último piloto acá y ya tiene su módulo en
// el catálogo (`cdci`, 37), así que lo gobierna la matriz RBAC como el resto.
//
// Este mecanismo queda para pilotos futuros que todavía no estén en TAB_MODULES.
// Es deuda por definición: cambiar quién entra exige un deploy.
const ROUTE_ALLOWED_EMPLOYEE_IDS = {};

// ===========================================================================
// Map de prefijos de ruta → moduleKey del catálogo (control.TAB_MODULES).
//
// Reorganizado 2026-08-31 contra el catálogo REAL de 38 módulos. Cada key de la
// derecha existe en TAB_MODULES; el número del comentario es su NUM_ID_MODULE,
// para poder cotejar de un vistazo contra Administration → Access Control.
//
// REGLAS
// 1. La key debe coincidir EXACTAMENTE con VAR_MODULE_KEY. Una key inexistente
//    no "abre" la ruta: la CIERRA para todos menos admin/CEO, porque
//    `modules.find(...)` no encuentra nada y hasAccess queda en false. Es un
//    fallo silencioso — de ahí que el test de rbacRouteCoverage valide las keys
//    contra este catálogo.
// 2. Gana el prefijo MÁS LARGO. Por eso /clinical-info/discharged puede tener
//    su propio módulo aunque /clinical-info esté mapeado.
// 3. Una ruta sin entrada acá queda ABIERTA a cualquier autenticado. Las
//    excepciones deliberadas están al final, listadas y justificadas.
//
// El orden sigue el del sidebar (config/navigationConfig.js) para que agregar
// un módulo sea "buscar su sección y sumar la línea".
// ===========================================================================
export const ROUTE_TO_MODULE_KEY = {
  // ── Top Bar ──────────────────────────────────────────────────────────────
  "/patientMap": "patient_map", //                                          (1)
  "/patient-map": "patient_map",
  "/PatientMap": "patient_map",
  "/patient-detail": "patient_map",

  // ── Clinical ─────────────────────────────────────────────────────────────
  "/care-gaps": "care_gaps", //                                             (3)
  "/care-gaps-v2": "care_gaps", // alias que redirige a /care-gaps
  "/CareGap": "care_gaps",
  "/coding-opportunities": "coding_opportunities", //                       (2)
  "/coding-opportunities-v2": "coding_opportunities",
  "/CoddingOpportunities": "coding_opportunities",
  "/ClinicalNoteAi": "ai_clinical_notes", //                               (16)
  "/clinical-notes-transcriptor": "clinical_notes_transcriptor", //        (34)
  "/MedicationReconciliationPage": "medication_reconciliation", //         (17)
  "/specialist-referrals": "specialist_referrals", //                       (6)
  "/SpecialistReferralForm": "specialist_referrals",
  "/SpecialistReferralDashboard": "specialist_referrals",
  "/lab-phlebotomy": "lab_phlebotomy", //                                   (7)
  "/LabPhlebotomy": "lab_phlebotomy",
  "/assignments": "assignments", //                                        (14)
  "/Assignment": "assignments",
  "/patient-geography": "patient_geography", //                            (38)

  // ── Care Management ──────────────────────────────────────────────────────
  // Cada censo tiene módulo propio en el catálogo, así que se puede conceder
  // Discharged sin conceder Admitted. El prefijo más largo gana sobre
  // /clinical-info, que queda como paraguas de la ruta raíz del módulo.
  "/clinical-info": "clinical_info", //                                     (4)
  "/ClinicalInfo": "clinical_info",
  "/clinicalInfo": "clinical_info",
  "/clinical-info/discharged": "discharged_census", //                     (10)
  "/clinical-info/admitted": "admitted_census", //                         (11)
  "/clinical-info/emergency": "emergency_census", //                       (12)
  "/clinical-info/high-risk": "high_risk", //                              (13)
  "/clinical-info/high-cost": "high_cost", // ruta legacy del mismo módulo (35)
  "/high-cost": "high_cost", //                                            (35)
  "/no-recent-visits": "no_recent_visits", //                              (36)
  "/care-plan": "care_plans", //                                            (5)
  "/care-plans": "care_plans",
  "/create-care-plan": "care_plans",
  "/Careplan": "care_plans",
  "/IACarePlanPage": "care_plans",
  "/patient-profiles": "patient_profiles", //                              (18)

  // ── Contact Center ───────────────────────────────────────────────────────
  "/call-center-intake": "call_center", //                                 (24)
  "/new-members": "new_members", //                                        (28)
  "/inactives": "inactive_members", //                                     (31)
  "/welcome-oep": "welcome_campaign_oep", //                               (29)
  "/call-assignments": "call_assignments", //                              (30)
  // Transportation está oculto (el módulo exporta [] y la ruta no se monta);
  // el mapeo queda listo para cuando se reactive.
  "/transportation": "transportation", //                                  (27)
  // PENDIENTE: No Recent Visit (call center) no tiene módulo propio. El
  // catálogo trae un solo `no_recent_visits` (36) y lo usa la lista CLÍNICA de
  // Care Management, que es otra pantalla. Mientras tanto hereda call_center.
  "/call-recent-visit": "call_center", //                                  (24)

  // ── Notas clínicas (fuera del sidebar, alcanzables por URL) ──────────────
  "/clinical-notes": "clinical_notes", //                                  (33)
  "/clinical-notes-v2": "clinical_notes", // wizard v2 — hueco cerrado 2026-08-31
  "/ClinicalNotesList": "clinical_notes",
  "/CreateClinicalNote": "clinical_notes",
  "/CreateClinicalNotePage": "clinical_notes",

  // ── Visit Cycle (Etapa 2) ────────────────────────────────────────────────
  // Los tres módulos del ciclo de visita gatean SOLO por acá. El 2026-08-31 se
  // retiraron sus allowlists de empleados (REACT_APP_PREVISIT_EMPLOYEES /
  // _GEOGRAPHY_): eran del piloto y quedaron como un segundo gate invisible que
  // hacía que conceder el módulo desde Access Control no alcanzara.
  "/previsit-planning": "previsit_planning", //                            (25)
  "/cdci": "cdci", //                                                      (37)
  // Módulo TEMPORAL de revisión de templates. Tiene backend y módulo propios
  // desde 2026-08-31; se borra junto con el módulo al cerrar la revisión.
  "/clinical-note-template-confirm": "note_template_confirm", //           (39)

  // ── General ──────────────────────────────────────────────────────────────
  "/general": "schedule", //                                               (19)
  "/tasks": "tasks", //                                                    (20)
  "/technical-issues": "technical_issues", //                              (21)

  // ── Administration ───────────────────────────────────────────────────────
  "/administration": "administration", //                                  (22)
  "/library": "library", //                                                (23)
  "/support": "tech_support", //                                            (8)

  // ── Tools ────────────────────────────────────────────────────────────────
  // ⚠️ `tools` NO EXISTE en el catálogo de 38 módulos. Por la regla 1, estas 14
  // rutas hoy están cerradas a todo el mundo salvo admin/CEO. El mapeo se
  // conserva a propósito: quitarlo las abriría a cualquier autenticado, que es
  // peor. Se arregla creando el módulo `tools` en TAB_MODULES.
  "/Tools": "tools",
  "/AHCA": "tools",
  "/PrePostAudi": "tools",
  "/PCPChanges": "tools",
  "/AddOn": "tools",
  "/NewMember": "tools",
  "/PostVisit": "tools",
  "/ACAAging6265": "tools",
  "/ACAAging65": "tools",
  "/WelcomeCampaign": "tools",
  "/ReportGeneratorPage": "tools",
  "/ClinicalDataCollectorPage": "tools",
  "/DataQualityValidationPage": "tools",
  "/report": "tools",

  // ── SIN MAPEAR a propósito (quedan abiertas a cualquier autenticado) ─────
  //
  // Etapa 2 / BillingV2 — el ítem del sidebar es dev-only pero la RUTA se monta
  // en producción (app/routes.js las spreadea sin condicional), así que hoy son
  // alcanzables por URL. No tienen módulo en el catálogo. Por cada una hay que
  // decidir: si va a producción → crear su moduleKey y mapearla acá; si no →
  // que su index.js exporte [] cuando el flag está apagado, como hacen
  // membership / cash / playground.
  //   /stage2-guide · /charge-capture · /denials-management · /post-discharge
  //   /high-risk-surveillance · /medication-adherence · /member-outreach
  //   /member-onboarding · /provider-doc-coach · /provider-performance
  //   /membership-analytics · /clinical-operations · /executive-financial
  //   /billing-dashboard
  //
  // /membership/* · /cash/* · /playground — sus módulos exportan [] en el build
  // de producción, así que la ruta no existe fuera de desarrollo.
};

const matchesPrefix = (pathname, route) =>
  pathname === route || pathname.startsWith(route + "/");

// Devuelve el moduleKey requerido para una ruta dada. Si más de un prefijo
// coincide, gana el más largo (match más específico).
const getRequiredModuleKey = (pathname) => {
  let bestPrefix = null;
  Object.keys(ROUTE_TO_MODULE_KEY).forEach((route) => {
    if (matchesPrefix(pathname, route)) {
      if (!bestPrefix || route.length > bestPrefix.length) bestPrefix = route;
    }
  });
  return bestPrefix ? ROUTE_TO_MODULE_KEY[bestPrefix] : null;
};

const isUniversalRoute = (pathname) =>
  UNIVERSAL_ROUTES.some((r) => matchesPrefix(pathname, r));

const isFullAccessRole = (user) => {
  if (!user) return false;
  if (FULL_ACCESS_ROLE_IDS.has(String(user.rol))) return true;
  const roleKey = user.activeRole?.key
    ? String(user.activeRole.key).toLowerCase()
    : null;
  return roleKey ? FULL_ACCESS_ROLE_KEYS.has(roleKey) : false;
};

/**
 * Verifica si el usuario puede acceder a una ruta.
 *
 * @param {Object} user - user object de AuthContext (incluye activeRole y modules)
 * @param {string} pathname - location.pathname
 * @returns {boolean}
 */
export const canUserAccessRoute = (user, pathname) => {
  // Piloto por lista de empleados: gana sobre todo lo demás (incluido el
  // bypass de admin/CEO). Si la ruta está en la lista, solo entran los
  // employeeId habilitados.
  const pilotRoute = Object.keys(ROUTE_ALLOWED_EMPLOYEE_IDS).find((route) =>
    matchesPrefix(pathname, route)
  );
  if (pilotRoute) {
    const employeeId = user?.employeeId ?? user?.employee ?? null;
    if (employeeId === null || employeeId === undefined || employeeId === "") return false;
    return ROUTE_ALLOWED_EMPLOYEE_IDS[pilotRoute].includes(String(employeeId));
  }

  if (isUniversalRoute(pathname)) return true;
  if (isFullAccessRole(user)) return true;

  // Denegación explícita por rol: gana sobre cualquier módulo concedido.
  const activeRoleKey = user?.activeRole?.key
    ? String(user.activeRole.key).toLowerCase()
    : null;
  if (activeRoleKey) {
    const denied = Object.keys(ROUTE_DENIED_ROLE_KEYS).some(
      (route) =>
        matchesPrefix(pathname, route) &&
        ROUTE_DENIED_ROLE_KEYS[route].includes(activeRoleKey)
    );
    if (denied) return false;
  }

  const requiredModuleKey = getRequiredModuleKey(pathname);
  // Ruta sin módulo asociado: no hay gate, queda abierta a cualquier
  // usuario autenticado.
  if (!requiredModuleKey) return true;

  const modules = Array.isArray(user?.modules) ? user.modules : [];
  // Si el user todavía no tiene módulos (post-login en curso o usuario
  // multi-rol que aún no eligió rol) no bloqueamos: ProtectedRoute /
  // LoginPage manejarán el siguiente paso.
  if (modules.length === 0) return true;

  const found = modules.find(
    (m) => m && m.key && String(m.key).toLowerCase() === requiredModuleKey
  );
  return Boolean(found?.hasAccess);
};

/**
 * Compatibilidad con la firma antigua. La nueva lógica vive en
 * canUserAccessRoute(user, pathname); este wrapper queda para no romper
 * callers existentes mientras se migran.
 *
 * @param {string|number} userRol - legacy user.rol
 * @param {string} pathname
 * @returns {boolean}
 */
export const canRoleAccessRoute = (userRol, pathname) => {
  return canUserAccessRoute({ rol: userRol }, pathname);
};
